import type { MouseEvent, ReactNode } from 'react';
import { Box, IconButton, Tooltip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

// ── Layout tokens ────────────────────────────────────────────────────────────
// Defined here rather than in the theme — these are component-internal geometry,
// not global design tokens. All sizes derive from theme.spacing() so they scale
// consistently with the rest of the app.
//
// spacing base = 8px:
//   spacing(0.25) = 2px   gap between buttons / container padding
//   spacing(0.75) = 6px   button border-radius
//   spacing(1)    = 8px   group container border-radius
//   spacing(2)    = 16px  progress indicator size
//   spacing(2.25) = 18px  svg icon size
//   spacing(2.5)  = 20px  separator height
//   spacing(4)    = 32px  button hit-target (WCAG 2.5.5 minimum)

export type RowActionVariant = 'primary' | 'error';

// ── RowActionButton ──────────────────────────────────────────────────────────

export interface RowActionButtonProps {
  title: string;
  ariaLabel: string;
  onClick: (e: MouseEvent) => void;
  disabled?: boolean;
  variant?: RowActionVariant;
  'aria-controls'?: string;
  'aria-haspopup'?: true | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
  'aria-expanded'?: boolean;
  children: ReactNode;
}

export function RowActionButton({
  title,
  ariaLabel,
  onClick,
  disabled = false,
  variant = 'primary',
  'aria-controls': ariaControls,
  'aria-haspopup': ariaHaspopup,
  'aria-expanded': ariaExpanded,
  children,
}: RowActionButtonProps) {
  const theme = useTheme();

  const hoverColor =
    variant === 'error' ? theme.palette.error.main : theme.palette.primary.main;

  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          aria-label={ariaLabel}
          aria-controls={ariaControls}
          aria-haspopup={ariaHaspopup}
          aria-expanded={ariaExpanded}
          disabled={disabled}
          onClick={e => { e.stopPropagation(); onClick(e); }}
          sx={{
            width: theme.spacing(4),
            height: theme.spacing(4),
            borderRadius: theme.spacing(0.75),
            color: 'text.secondary',
            transition: theme.transitions.create(
              ['background-color', 'color', 'box-shadow'],
              { duration: theme.transitions.duration.shortest },
            ),
            '& .MuiSvgIcon-root': { fontSize: theme.spacing(2.25) },
            '&:hover': {
              bgcolor: theme.palette.common.white,
              color: hoverColor,
              boxShadow: `0 1px 4px ${alpha(theme.palette.common.black, 0.12)}`,
            },
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: '1px',
            },
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

// ── RowActionSeparator ───────────────────────────────────────────────────────

export function RowActionSeparator() {
  const theme = useTheme();
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: '1px',
        height: theme.spacing(2.5),
        bgcolor: alpha(theme.palette.common.black, 0.10),
        flexShrink: 0,
      }}
    />
  );
}

// ── RowActionGroup ───────────────────────────────────────────────────────────

export interface RowActionGroupProps {
  fullWidth?: boolean;
  children: ReactNode;
}

export function RowActionGroup({ fullWidth = false, children }: RowActionGroupProps) {
  const theme = useTheme();

  if (fullWidth) {
    return (
      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-around', alignItems: 'center' }}>
        {children}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: theme.spacing(0.25),
          p: theme.spacing(0.25),
          borderRadius: theme.spacing(1),
          transition: theme.transitions.create('background-color', {
            duration: theme.transitions.duration.shortest,
          }),
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
